import React, { useCallback } from "react";
import mapboxgl from "mapbox-gl";

import { unselectFeature } from "fond/project/redux";
import { Configuration } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppDispatch } from "fond/utils/hooks";

import MapPopup from "../MapPopup";

import FeaturesList from "./FeaturesList";

import { PopupContainer } from "./FeaturesPopup.styles";

interface IProps {
  config: Configuration;
  features: mapboxgl.MapboxGeoJSONFeature[];
  lngLat: mapboxgl.LngLatLike;
  layerConfigs: Array<LayerConfig | SublayerConfig>;
  onClose(): void;
  onFeatureClick(feature: mapboxgl.MapboxGeoJSONFeature): void;
}

/**
 * Provides the user with a selection of features to select from
 * For example, this is used for feature selection when the user
 * clicks on the map & there are stacked features or features in close proximity.
 */
const FeaturesPopup: React.FC<IProps> = ({ config, features, layerConfigs, lngLat, onClose, onFeatureClick }: IProps) => {
  const dispatch = useAppDispatch();

  /**
   * Opens the individual feature once a selection has been made
   */
  const handleOnClick = useCallback(
    (feature: mapboxgl.MapboxGeoJSONFeature) => {
      onFeatureClick(feature);
    },
    [onFeatureClick]
  );

  /**
   * Callback function called when the popup closes
   */
  const handleClose = useCallback(
    ({ type }: { type: string }) => {
      if (type === "manual-close") {
        dispatch(unselectFeature());
      }
    },
    [dispatch]
  );

  return (
    <MapPopup lngLat={lngLat} onClose={handleClose} title="Select a Feature">
      <PopupContainer className="customScrollbars" data-testid="feature-selections">
        <FeaturesList features={features || []} config={config} layerConfigs={layerConfigs} onFeatureClick={handleOnClick} />
      </PopupContainer>
    </MapPopup>
  );
};

export default FeaturesPopup;
